import React, { useState } from "react";

import type { ValidateErrorEntity } from "rc-field-form/lib/interface";

import { checkPassword, getUserSettings, signinUser, updateUserSetting } from "services";

import {
  clearLocalKlimVar,
  getLocalKlimVar,
  incompleteMessage,
  mapAppTypeToString,
  setLocalKlimVar,
  useAppType,
  useNavigate,
  useToggleNavs,
  useTranslation,
} from "utils";

import { ErrorBoundary, Form, MainPage, ScreenSplit, Message as message } from "components";

import { SignInButtons } from "pages/app/account/components/SignInButtons";
import { SignInInputs } from "pages/app/account/components/SignInInputs";
import { TestimonialScreen } from "pages/app/account/components/TestimonialScreen";

interface SignInFormValues {
  email: string;
  password: string;
}

interface ShareData {
  title: string;
  summary: string;
  link: string;
  image: string;
}

interface SignInResponse {
  token: string;
}

interface UserSettingsResponse {
  appAlertHidden?: boolean;
  referralAlert?: number;
}

export const SignIn = () => {
  const { t } = useTranslation(["signinup", "meta"]);

  const [loading, setLoading] = useState<boolean>(false);
  const appType = useAppType();
  const navigate = useNavigate();
  useToggleNavs(false, appType.mobile);

  const [share] = useState<ShareData>({
    title: t("meta:signIn.title"),
    summary: t("meta:signIn.description"),
    link: `${process.env.REACT_APP_FARM_URL}/sign-in`,
    image: `${process.env.REACT_APP_FARM_URL}/images/misc/share.png`,
  });

  const completeSignin = (res: SignInResponse): void => {
    setLocalKlimVar("token", res.token);
    const urlSlice = getLocalKlimVar("urlSlice");
    const controller = new AbortController();

    getUserSettings({
      signal: controller.signal,
    })
      .then((res) => {
        if (!res) {
          return;
        }

        const settings = res as unknown as UserSettingsResponse;

        if (appType.desktop || settings.appAlertHidden) {
          const currentAlert = settings.referralAlert || 0;
          updateUserSetting({
            item: "referralAlert",
            value: currentAlert < 4 ? currentAlert + 1 : currentAlert,
          });
        }
      })
      .finally(() => {
        message({ content: t("signin.success") });
        if (urlSlice) {
          clearLocalKlimVar("urlSlice");
          navigate(`/${urlSlice}`);
        } else {
          navigate("/dashboard");
        }
      });
  };

  const confirmPassword = (password: string, tempToken: string): void => {
    const controller = new AbortController();
    setLoading(true);

    checkPassword({
      password: password,
      token: tempToken,
      signal: controller.signal,
    }).then((res) => {
      completeSignin(res);
    });
  };

  const complete = (v: SignInFormValues): void => {
    const controller = new AbortController();
    setLoading(true);

    signinUser({
      appType: mapAppTypeToString(appType),
      email: v.email,
      signal: controller.signal,
    })
      .then((res) => {
        confirmPassword(v.password, res.token);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const incomplete = (v: ValidateErrorEntity<SignInFormValues>): void => {
    if (!v.values.email || !v.values.password) {
      message({
        content: t("errors.mandatoryFields"),
        icon: "error",
      });
      return;
    }

    if (!v.values.email.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/)) {
      message({
        content: t("errors.invalidEmailSignIn"),
        icon: "error",
      });
      return;
    }

    incompleteMessage(t("errors.mandatoryFields"));
  };

  return (
    <ScreenSplit sideChildren={<TestimonialScreen farmerType="default" />}>
      <MainPage loading={false} pageName={"sign-in-up"} share={share} containerClass={appType.mobile ? "small" : "margin-top-0"}>
        <div className="mini-form">
          <h2 className="margin-top-200">{t("signin.title")}</h2>
          <p className="margin-top-100 margin-bottom-0">{t("signin.description")}</p>
          <Form name="signin" className="margin-top-400" onFinish={(v) => complete(v)} onFinishFailed={incomplete}>
            <ErrorBoundary>
              <SignInInputs />
            </ErrorBoundary>
            <ErrorBoundary>
              <SignInButtons loading={loading} />
            </ErrorBoundary>
          </Form>
        </div>
      </MainPage>
    </ScreenSplit>
  );
};
